import heic2any from 'heic2any';

const HEIC_TYPES = ['image/heic', 'image/heif'];
const HEIC_EXTENSIONS = ['.heic', '.heif'];

function isHeicFile(file: File): boolean {
  if (HEIC_TYPES.includes(file.type.toLowerCase())) return true;
  const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  return HEIC_EXTENSIONS.includes(ext);
}

/**
 * Converts HEIC/HEIF images to JPEG so they render in all browsers.
 * Non-HEIC files are returned as-is.
 */
export async function ensureBrowserCompatibleImage(file: File): Promise<File> {
  if (!isHeicFile(file)) return file;

  const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const converted = Array.isArray(blob) ? blob[0] : blob;

  const newName = file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg');
  return new File([converted], newName, { type: 'image/jpeg' });
}

export const IMAGE_LIMITS = {
  cover: {
    minWidth: 1344,
    minHeight: 320,
    maxWidth: 2400,
    maxHeight: 572,
    ratio: 1344 / 320,
  },
  logo: {
    minWidth: 200,
    minHeight: 200,
    maxWidth: 600,
    maxHeight: 600,
    ratio: 1,
  },
} as const;

export interface ImageBounds {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
}

export interface ConformResult {
  file: File;
  wasAdjusted: boolean;
  originalWidth: number;
  originalHeight: number;
}

const RESIZE_QUALITY = 0.92;

/**
 * Letterbox-pads an image to the target aspect ratio without stretching,
 * then scales to fit within min/max bounds. Returns the original file
 * unchanged when it already matches ratio and size requirements.
 */
export function padImageToAspectRatio(
  file: File,
  targetRatio: number,
  bounds: ImageBounds,
  backgroundColor = '#FFFFFF',
): Promise<ConformResult> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const sourceRatio = w / h;

      let canvasW: number;
      let canvasH: number;
      if (sourceRatio > targetRatio) {
        canvasW = w;
        canvasH = w / targetRatio;
      } else {
        canvasH = h;
        canvasW = h * targetRatio;
      }

      const paddingNeeded =
        Math.abs(canvasW - w) > 0.5 || Math.abs(canvasH - h) > 0.5;

      let scale = 1;
      let upscaled = false;

      if (canvasW < bounds.minWidth || canvasH < bounds.minHeight) {
        scale = Math.max(bounds.minWidth / canvasW, bounds.minHeight / canvasH);
        upscaled = true;
      }

      let finalW = Math.round(canvasW * scale);
      let finalH = Math.round(canvasH * scale);

      if (finalW > bounds.maxWidth || finalH > bounds.maxHeight) {
        const downScale = Math.min(bounds.maxWidth / finalW, bounds.maxHeight / finalH);
        scale *= downScale;
        finalW = Math.round(canvasW * scale);
        finalH = Math.round(canvasH * scale);
      }

      const wasAdjusted = paddingNeeded || upscaled;

      if (!wasAdjusted && w <= bounds.maxWidth && h <= bounds.maxHeight) {
        URL.revokeObjectURL(img.src);
        resolve({ file, wasAdjusted: false, originalWidth: w, originalHeight: h });
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = finalW;
      canvas.height = finalH;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(img.src);
        reject(new Error('Canvas context unavailable'));
        return;
      }

      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, finalW, finalH);

      const drawW = Math.round(w * scale);
      const drawH = Math.round(h * scale);
      const offsetX = Math.round((finalW - drawW) / 2);
      const offsetY = Math.round((finalH - drawH) / 2);

      ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
      URL.revokeObjectURL(img.src);

      const keepPng = file.type === 'image/png';
      const mimeType = keepPng ? 'image/png' : 'image/jpeg';
      const quality = keepPng ? undefined : RESIZE_QUALITY;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas toBlob failed'));
            return;
          }
          const ext = keepPng ? '.png' : '.jpg';
          const baseName = file.name.replace(/\.[^.]+$/, '');
          resolve({
            file: new File([blob], `${baseName}${ext}`, { type: mimeType }),
            wasAdjusted,
            originalWidth: w,
            originalHeight: h,
          });
        },
        mimeType,
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for padding'));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Downscale an image to fit within maxWidth x maxHeight while preserving
 * aspect ratio. Images already within bounds are returned as-is.
 * PNGs with transparency are kept as PNG; everything else becomes JPEG.
 */
export function resizeImageFile(
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { naturalWidth: w, naturalHeight: h } = img;

      if (w <= maxWidth && h <= maxHeight) {
        URL.revokeObjectURL(img.src);
        resolve(file);
        return;
      }

      const scale = Math.min(maxWidth / w, maxHeight / h);
      const targetW = Math.round(w * scale);
      const targetH = Math.round(h * scale);

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(img.src);
        reject(new Error('Canvas context unavailable'));
        return;
      }

      ctx.drawImage(img, 0, 0, targetW, targetH);
      URL.revokeObjectURL(img.src);

      const keepPng = file.type === 'image/png';
      const mimeType = keepPng ? 'image/png' : 'image/jpeg';
      const quality = keepPng ? undefined : RESIZE_QUALITY;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas toBlob failed'));
            return;
          }
          const ext = keepPng ? '.png' : '.jpg';
          const baseName = file.name.replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${baseName}${ext}`, { type: mimeType }));
        },
        mimeType,
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = URL.createObjectURL(file);
  });
}
